/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DialogModule
 */

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';

import './avi-suppress-warnings-dialog.component.less';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import { IAlertObject } from 'ng/shared/components/avi-alert-group/avi-alert-group.component';
import * as l10n from './avi-suppress-warnings-dialog.component.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component to display config warnings & enable users
 * to suppress them.
 *
 * @author Shahab Hashmi
 */
@Component({
    selector: 'avi-suppress-warnings-dialog',
    templateUrl: './avi-suppress-warnings-dialog.component.html',
})
export class AviSuppressWarningsDialogComponent implements OnInit {
    /**
     * Warning messages to be displayed.
     */
    @Input()
    public warnings: string[];

    /**
     * Event to be fired upon user Canceling.
     */
    @Output()
    private readonly onClose = new EventEmitter();

    /**
     * Event to be fired upon user Confirming continue.
     */
    @Output()
    private readonly onConfirm = new EventEmitter<boolean>();

    /**
     * Store modified footer text to be displayed on UI.
     */
    public footerTextHTML = '';

    /**
     * Flag to suppress warnings.
     */
    public suppressWarnings = false;

    /**
     * Object to be passed with required props to AviAlertGroupComponent.
     */
    public warningList: IAlertObject[] = [];

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    constructor(private readonly l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.warnings.forEach((warning: string) => this.warningList.push({
            message: warning,
            status: 'warning',
        }));

        const configWarningslocationText = this.l10nService.getMessage(l10nKeys.configurationWarningslocationLabel);

        this.footerTextHTML = this.l10nService.getMessage(l10nKeys.footerMessage, [configWarningslocationText]);
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        changes.warnings?.currentValue.forEach((warning: string) => this.warningList.push({
            message: warning,
            status: 'warning',
        }));
    }

    public handleCancel(): void {
        this.onClose.emit();
    }

    public handleConfirm(): void {
        this.onConfirm.emit(this.suppressWarnings);
    }
}
